import Vue from 'vue';
import { toast } from '@mx/vix';
import { env, Runtime } from './env';
import { monitor } from './monitor';
import { CfcaCertRequestCodeType } from './constant';
import { msi } from '@mtfe/msi-knb-adaptor';

class Bridge {
  isNative(): boolean {
    return env.isNative;
  }

  setTitle(title: string) {
    document.title = title;
    KNB.ready(() => {
      KNB.setTitle({ title });
    });
  }

  // 设置返回按钮
  setLLButton(handle: () => void, content?: string) {
    let llButtonConfig = { handle } as KNBLLButtonConfig;
    if (!content) {
      llButtonConfig = { ...llButtonConfig, icon: 'H5_Back' };
    } else if (this.isBase64(content)) {
      llButtonConfig = { ...llButtonConfig, type: 'base64', icon: content };
    } else {
      llButtonConfig = { ...llButtonConfig, type: 'text', text: content };
    }
    KNB.ready(() => {
      KNB.setLLButton(llButtonConfig);
    });
  }

  // 设置右上角按钮
  setRRButton(handle: () => void, content: string) {
    let rrButtonConfig = { handle, position: 'RR' } as KNBNavButtonsConfig;
    if (this.isBase64(content)) {
      rrButtonConfig = { ...rrButtonConfig, type: 'base64', icon: content };
    } else {
      rrButtonConfig = { ...rrButtonConfig, type: 'text', text: content };
    }
    KNB.ready(() => {
      KNB.setNavButtons(rrButtonConfig);
    });
  }

  // 禁止页面反弹
  setBouncesUnabled() {
    if (env.isNative && !env.isHarmony) {
      KNB && KNB.ready(() => {
        KNB.setBouncesEnabled({
          enabled: 0 // 1表示允许反弹，0表示禁止。default: 1
        });
      });
    }
  }

  resetNavButton() {
    KNB.ready(() => {
      KNB.setNavButtons({
        position: 'RR',
        disable: 1
      });
    });
  }

  // 登录
  login(): void {
    const loginTimeKey = 'payfe_login_time';
    const loginTime = Number(sessionStorage.getItem(loginTimeKey)) || 1;
    sessionStorage.setItem(loginTimeKey, loginTime + 1 + '');

    if (loginTime > 3) {
      setTimeout(() => {
        toast.show('text', '登录状态有点问题，请稍后再试');
      }, 0);
      sessionStorage.removeItem(loginTimeKey);
      return;
    }

    KNB.ready(() => {
      if (env.isNative) {
        this.knbLogin();
      } else {
        this.webLogin();
      }
    });
  }
  webLogin(): void {
    location.href = `https://i.meituan.com/account/login?backurl=${encodeURIComponent(
      location.href
    )}`;
  }
  knbLogin(): void {
    KNB.ready(() => {
      KNB.login({
        success: () => {
          location.reload();
        }
      });
    });
  }

  getAppInfo(): Promise<any> {
    if (env.isNative && env.isHarmony) {
      return new Promise((resolve, reject) => {
        KNB.ready(() => {
          msi.getSystemInfo({
            success: (res) => {
              resolve(res);
            },
            fail: (err) => {
              reject(err);
            }
          });
        });
      });
    } else {
      return new Promise((resolve, reject) => {
        KNB.ready(() => {
          KNB.getAppInfo({
            success: (appInfo: GetAppInfoResult) => {
              resolve(appInfo);
            },
            fail(error: Error) {
              reject(error);
            }
          });
        });
      });
    }
  }

  // 获取设备信息
  getDeviceInfo(): Promise<any> {
    return new Promise((resolve, reject) => {
      KNB.ready(() => {
        KNB.getDeviceInfo({
          sceneToken: 'jf-20592152527f3a4d',
          success: (data: any) => {
            resolve(data);
          },
          fail(error: Error) {
            reject(error);
          }
        });
      });
    });
  }

  // 设置风控信息
  getFingerprint(): Promise<string> {
    return new Promise((resolve, reject) => {
      KNB.ready(() => {
        KNB.getFingerprint({
          success: (data: any) => {
            resolve(data.fingerprint);
          },
          fail() {
            reject();
          }
        });
      });
    });
  }

  // 获取生物识别信息
  getBiometricsInfo(): Promise<any> {
    return new Promise((resolve, reject) => {
      bridge.useNative('pay.getBiometricsInfo', {
        success: (data: any) => {
          resolve(data);
        },
        fail: (error: any) => {
          reject(error);
        }
      });
    });
  }

  // 获取uuid
  getUserInfo(): Promise<any> {
    return new Promise((resolve, reject) => {
      KNB.ready(() => {
        KNB.getUserInfo({
          success: (user: any) => {
            resolve(user);
          },
          fail: (error: any) => {
            reject(error);
          }
        });
      });
    });
  }

  // 改变指纹支付状态
  changeFingerPay(
    params: any,
    success: (response: any) => {},
    fail: (error: any) => {}
  ) {
    bridge.useNative('pay.changeFingerMeituanPay', {
      ...params,
      success,
      fail
    });
  }

  // 没有指纹信息调起系统设置
  setFingerPay(success: (response: any) => {}, fail: (error: any) => {}) {
    bridge.useNative('pay.setFingerMeituanPay', {
      success,
      fail
    });
  }

  // 打开新页面
  openPage(url: string, notitlebar: number = 0) {
    if (!/^(http|https):\/\//.test(url)) {
      if (env.runtime === Runtime.Local || env.runtime === Runtime.Proxy) {
        url = location.origin + '/' + url;
      } else {
        url = location.origin + '/resource/pay-settings/' + url;
      }
    }
    if (this.isNative()) {
      KNB.ready(() => {
        KNB.openPage({
          url,
          query: {
            _knbopeninapp: 1,
            noquery: 1,
            notitlebar
          }
        });
      });
    } else {
      location.href = url;
    }
  }

  // 指定跳转至当前容器
  jumpPage(url: string) {
    KNB.ready(() => {
      KNB.jumpPage({
        url,
        query: {
          _knbopeninapp: 1
        }
      });
    });
  }

  closePage(): void {
    KNB.ready(() => {
      KNB.closePage({});
    });
  }
  setStorage(key: string, value: string): void {
    KNB.ready(() => {
      if (KNB.setStorage) {
        KNB.setStorage({
          key,
          value,
          level: 0
        });
      }
    });
  }

  getStorage(key: string, success: (res: any) => void): void {
    KNB.ready(() => {
      KNB.getStorage({
        key,
        success,
        fail: () => {}
      });
    });
  }

  subscribe(
    action: string,
    handle: () => void,
    success?: (result?: { subId?: string }) => void
  ) {
    KNB.ready(() => {
      KNB.subscribe({
        action,
        handle,
        success
      });
    });
  }

  unSubscribe(action: string, success: () => void, subId?: string) {
    const params: Record<string, any> = {
      action,
      success
    };
    if (subId) {
      params.subId = subId;
    }
    KNB.ready(() => {
      KNB.unsubscribe(params);
    });
  }

  // 找回密码
  retrievePassword() {
    this.openPage('https://npay.meituan.com/resource/iwallet/verify.html');
  }

  // 发送 Result 到 Native
  setResultToNative({
    resultCode,
    resultData,
    success,
    fail
  }: KNBSetResultConfig) {
    KNB.ready(() => {
      KNB.setResult({
        resultCode,
        resultData,
        success,
        fail
      });
    });
  }

  getResult(
    success: (result: Record<string, any>) => void,
    fail: (err: unknown) => void
  ) {
    KNB.ready(() => {
      KNB.getResult({
        success,
        fail
      });
    });
  }

  openPageForResult(url: string) {
    return new Promise((resolve, reject) => {
      KNB &&
        KNB.ready(() => {
          // KNBP 对象不支持 openPageForResult()
          KNB.openPageForResult({
            url,
            success: resolve,
            fail: reject
          });
        });
    });
  }

  // 获取生物识别信息
  getLocation(): Promise<any> {
    return new Promise((resolve, reject) => {
      KNB.ready(() => {
        KNB.getLocation({
          sceneToken: 'jf-4b58aa4469ef6adb',
          success: (location) => {
            if (location.lat === 0 && location.lng === 0) {
              monitor.captureMessage({
                name: 'knb_location_success_lat&lng为null或者0',
                msg: JSON.stringify(location)
              });
            }
            resolve(location);
          },
          fail: (error) => {
            monitor.captureMessage({
              name: 'knb_location_fail',
              msg: JSON.stringify(error)
            });
            reject(error);
          }
        });
      });
    });
  }

  // 获取cfca信息 https://km.sankuai.com/page/1339245163
  getCfcaCert(requestCode: CfcaCertRequestCodeType): Promise<any> {
    return new Promise((resolve, reject) => {
      KNB &&
        KNB.ready(() => {
          bridge.useNative('pay.enterCertificateProcess', {
            request_code: requestCode,
            success: (res: any) => {
              resolve(res);
            },
            fail: (error: any) => {
              monitor.captureMessage({
                name: 'pay.enterCertificateProcess调桥失败',
                msg: JSON.stringify(error)
              });
              reject(error);
            }
          });
        });
    });
  }

  // 获取客户端组件信息
  getServiceInfo(): Promise<any> {
    return new Promise((resolve, reject) => {
      KNB.ready(() => {
        KNB.getServiceInfo({
          name: 'payment',
          success: (res: any) => {
            resolve(res);
          },
          fail: (error: any) => {
            monitor.captureMessage({
              name: 'getServiceInfo调桥失败',
              msg: JSON.stringify(error)
            });
            reject(error);
          }
        });
      });
    });
  }

  // 调用 Native Bridge Function
  private useNative(knbFunc: string, config: object) {
    KNB.ready(() => {
      KNB.use(knbFunc, config);
    });
  }

  private isBase64(content: string): boolean {
    return content.indexOf('data:image') === 0;
  }
}

export const bridge = new Bridge();

// extend Vue prototype
declare module 'vue/types/vue' {
  interface Vue {
    $bridge: typeof bridge;
  }
}

Vue.prototype.$bridge = bridge;
